import React from 'react';
import TrustButVerify from './TrustButVerify';
import { ConfirmWaterReport, UnsubscribeWaterReport } from './components/EmailLandingPages';

// ─── /report EMAIL DEEP LINK, WEB FALLBACK (Sep 1 2026) ─────────────────────
// The Monthly Water Report email CTA points at /report?zip=NNNNN&src=... so
// iOS can hand the tap to the native WTR App (see the /report component in
// public/.well-known/apple-app-site-association). Everyone else, Android,
// desktop, and any mail client that suppresses universal links, lands HERE.
// TrustButVerify already listens for the wtr-navigate CustomEvent and runs
// startScan on detail.scan, so this wrapper renders the normal landing and
// dispatches that event with the emailed ZIP. Effect ordering makes the
// dispatch safe without a timer: TrustButVerify is the child, child effects
// commit before parent effects, so its listener is attached by the time this
// runs. An invalid or missing ZIP degrades to the plain landing page, which
// is the honest fallback: never fake a scan the link did not specify.
function ReportFromEmail() {
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const zip = params.get('zip') || '';
    const src = params.get('src') || 'link';
    // SHARED REPORTS (2026-10-01). A report shared from the native WTR App
    // carries the utility the sender was viewing, so a ZIP with several
    // utilities (92236 has at least three) lands the recipient on the same
    // one, not the ZIP default. Optional: monthly email links carry no pwsid
    // and keep working unchanged. Validated to the same shape the API accepts.
    const pwsidRaw = (params.get('pwsid') || '').trim().toUpperCase();
    const pwsid = /^[A-Z0-9]{2,3}\d{6,10}$/.test(pwsidRaw) ? pwsidRaw : null;
    if (!/^\d{5}$/.test(zip)) return;
    if (window.posthog) {
      window.posthog.capture('report_deeplink_web_fallback', { zip, src, pwsid });
    }
    window.dispatchEvent(new CustomEvent('wtr-navigate', { detail: { scan: zip, pwsid } }));
  }, []);
  return <TrustButVerify />;
}

// ─── ERROR BOUNDARY ──────────────────────────────────────────────────────────
// Catches any unhandled React render error and shows a fallback UI
// instead of a blank white screen.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to PostHog if available
    if (window.posthog) {
      window.posthog.capture('app_error_boundary', {
        error_message: error?.message,
        component_stack: info?.componentStack?.slice(0, 500)
      });
    }
    console.error('[WTR App] ErrorBoundary caught:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 32,
          fontFamily: "'Nunito', 'Helvetica Neue', sans-serif",
          textAlign: 'center',
          background: '#FFFFFF'
        }}>
          <div style={{ fontSize: 48, marginBottom: 16, display: 'flex', justifyContent: 'center' }}><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#51B0E6" strokeWidth="1.5"><path d="M12 2C12 2 6 10 6 14a6 6 0 0012 0c0-4-6-12-6-12z"/></svg></div>
          <h1 style={{ fontSize: 20, fontWeight: 900, color: '#0A1A2E', marginBottom: 8 }}>
            Something went wrong
          </h1>
          <p style={{ fontSize: 13, color: '#A6A8AB', marginBottom: 20, maxWidth: 300, lineHeight: 1.6 }}>
            The WTR App encountered an issue. Please refresh to try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: 'linear-gradient(135deg, #51B0E6, #2A8FCA)',
              color: '#fff',
              border: 'none',
              padding: '12px 28px',
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 800,
              cursor: 'pointer'
            }}
          >
            REFRESH APP
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  // Email-landing paths (Jul 17 2026 fix). The SPA fallback in vercel.json
  // sends every path to this app; until this switch existed the confirm and
  // unsubscribe links from report emails loaded the normal landing page and
  // did nothing, which left every subscriber at opt_in_status 'pending'.
  const path = window.location.pathname;
  if (path === '/confirm-water-report') {
    return (
      <ErrorBoundary>
        <ConfirmWaterReport />
      </ErrorBoundary>
    );
  }
  if (path === '/unsubscribe') {
    return (
      <ErrorBoundary>
        <UnsubscribeWaterReport />
      </ErrorBoundary>
    );
  }
  if (path === '/report') {
    return (
      <ErrorBoundary>
        <ReportFromEmail />
      </ErrorBoundary>
    );
  }
  return (
    <ErrorBoundary>
      <TrustButVerify />
    </ErrorBoundary>
  );
}

export default App;
